import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar, Footer } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { BackButton } from "@/components/BackButton";
import { ExternalLink, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { tierMeta, type AlertTier } from "@/lib/alertStyles";
import { cn } from "@/lib/utils";

interface PageRow {
  id: string;
  key: string;
  name: string;
  parent: string | null;
  path_pattern: string;
  sort_order: number;
}

interface AlertRow {
  id: string;
  tier: AlertTier;
  title: string;
  description: string;
  app_wide: boolean;
  page_keys: string[];
  active: boolean;
}

/**
 * Convert path_pattern with :params into a usable URL for navigation.
 * Replaces :params with placeholder values so the route resolves.
 */
function resolvePath(pattern: string): string {
  return pattern
    .replace(":id", "demo")
    .replace(":userId", "demo")
    .replace(":threadId", "demo")
    .replace(":areaId", "demo");
}

const tiers: AlertTier[] = ["notification", "alert", "urgent"];

export default function AlertsTest() {
  const [pages, setPages] = useState<PageRow[]>([]);
  const [alerts, setAlerts] = useState<AlertRow[]>([]);

  useEffect(() => {
    const load = async () => {
      const [{ data: p }, { data: a }] = await Promise.all([
        supabase.from("app_pages" as any).select("*").order("parent").order("sort_order"),
        supabase.from("system_alerts" as any).select("*").eq("active", true),
      ]);
      if (p) setPages(p as any);
      if (a) setAlerts(a as any);
    };
    load();
  }, []);

  // Group pages by parent
  const grouped = pages.reduce<Record<string, PageRow[]>>((acc, p) => {
    const k = p.parent || "Övrigt";
    (acc[k] = acc[k] || []).push(p);
    return acc;
  }, {});

  const appWideAlerts = alerts.filter((a) => a.app_wide);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container flex-1 py-8 max-w-5xl">
        <BackButton fallback="/admin" label="Tillbaka till adminpanelen" />
        <h1 className="mb-1 font-display text-2xl font-bold md:text-3xl">
          Banner-testsida
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Förhandsgranska tier-styling och navigera till sidor för att verifiera
          att aktiva systemmeddelanden visas korrekt per route.
        </p>

        {/* Tier preview */}
        <section className="mb-8">
          <h2 className="mb-3 font-display text-lg font-semibold">
            Tier-stilar (förhandsvisning)
          </h2>
          <div className="space-y-2">
            {tiers.map((tier) => {
              const meta = tierMeta[tier];
              const Icon = meta.icon;
              return (
                <div
                  key={tier}
                  className={cn(
                    "rounded-md border px-4 py-3 flex items-start gap-3",
                    meta.banner,
                  )}
                >
                  <Icon className={cn("h-5 w-5 mt-0.5 shrink-0", meta.iconColor)} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold leading-tight">
                      {meta.emoji} {meta.label}
                    </p>
                    <p className="text-sm leading-snug opacity-90 mt-0.5">
                      Exempel på meddelande av typen "{meta.label.toLowerCase()}".
                    </p>
                  </div>
                  <Badge variant="outline" className={meta.badge}>
                    {tier}
                  </Badge>
                </div>
              );
            })}
          </div>
        </section>

        <Separator className="my-8" />

        {/* Active alerts overview */}
        <section className="mb-8">
          <h2 className="mb-3 font-display text-lg font-semibold">
            Aktiva meddelanden i databasen ({alerts.length})
          </h2>
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Inga aktiva meddelanden. Skapa några i adminpanelen för att testa.
            </p>
          ) : (
            <div className="grid gap-2">
              {alerts.map((a) => {
                const meta = tierMeta[a.tier];
                return (
                  <Card key={a.id} className="p-3 flex items-center gap-3">
                    <Badge variant="outline" className={meta.badge}>
                      {meta.emoji} {meta.label}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {a.app_wide ? (
                          <span className="inline-flex items-center gap-1">
                            <Globe className="h-3 w-3" /> App-wide
                          </span>
                        ) : (
                          `${a.page_keys.length} sida(or): ${a.page_keys.join(", ")}`
                        )}
                      </p>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
          {appWideAlerts.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              ℹ️ {appWideAlerts.length} app-wide meddelande(n) visas på alla
              routes nedan.
            </p>
          )}
        </section>

        <Separator className="my-8" />

        {/* Routes navigation */}
        <section>
          <h2 className="mb-3 font-display text-lg font-semibold">
            Navigera till routes
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Klicka för att öppna en sida i ny flik och se vilka meddelanden som
            visas där.
          </p>
          <div className="grid gap-6">
            {Object.entries(grouped).map(([parent, items]) => (
              <div key={parent}>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  {parent}
                </h3>
                <div className="grid gap-2 sm:grid-cols-2">
                  {items.map((page) => {
                    const matchingAlerts = alerts.filter(
                      (a) => a.app_wide || a.page_keys.includes(page.key),
                    );
                    const path = resolvePath(page.path_pattern);
                    return (
                      <Card
                        key={page.id}
                        className="p-3 flex items-center justify-between gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {page.name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate font-mono">
                            {page.path_pattern}
                          </p>
                          {matchingAlerts.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {matchingAlerts.map((a) => (
                                <Badge
                                  key={a.id}
                                  variant="outline"
                                  className={cn(
                                    "text-[10px] px-1.5 py-0",
                                    tierMeta[a.tier].badge,
                                  )}
                                >
                                  {tierMeta[a.tier].emoji}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button asChild size="sm" variant="outline">
                          <Link to={path} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
